/**
 * MetricsTab — usage metrics dashboard (queries, users, activity).
 * Extracted verbatim from AdminScreen.jsx (god-file decomposition).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { db } from '../firebaseConfig';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { adminStyles as styles } from './adminStyles';
import { uiVar } from './floodlight/uiStyleCss';

export default function MetricsTab() {
  const [metrics, setMetrics] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d'); // '24h' | '7d' | '30d' | 'all'
  const [filterNoResults, setFilterNoResults] = useState(false);
  const [filterNoOddsData, setFilterNoOddsData] = useState(false);
  const [filterErrors, setFilterErrors] = useState(false); // ⭐ New: filter to show only errors
  const [expandedQueryId, setExpandedQueryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [aiSummary, setAiSummary] = useState(null); // ⭐ New: AI summary
  const [summaryLoading, setSummaryLoading] = useState(false);
  const itemsPerPage = 25;
  
  // ⭐ Function to translate log events to plain English
  const translateLogEvent = (log) => {
    const event = log.event || '';
    const translations = {
      'search_start': '🔍 Search started',
      'search_started': '🔍 Search started',
      'cache_check': '📦 Checking cache...',
      'cache_hit': '✅ Found in cache (fast path)',
      'cache_miss': '⏳ Cache miss - querying live API',
      'api_call': '🌐 Calling odds API',
      'api_response': `✅ API responded with ${log.resultsCount || log.itemsCount || 0} results`,
      'results_filtered': `🔄 Filtered to ${log.finalResultsCount || 0} relevant results`,
      'search_complete': `✅ Search complete in ${log.durationMs || 0}ms`,
      'search_completed': `✅ Search complete`,
      'fixtures_found_no_odds': '⚠️ Found games but no odds posted yet',
      'no_fixtures_found': '❌ No matching games found',
      'no_results': '❌ No results returned',
      'error': `❌ Error: ${log.error || 'Unknown error'}`,
      'llm_fallback': '🤖 Using AI to understand query',
      'llm_classification': '🤖 AI classified the query type',
      'fixture_watched': '👁️ User added to watchlist',
      'notification_sent': '📱 Notification sent'
    };
    
    // Check for partial matches
    for (const [key, translation] of Object.entries(translations)) {
      if (event.toLowerCase().includes(key.toLowerCase())) {
        return translation;
      }
    }
    
    // Return cleaned up event name if no translation
    return event.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // ⭐ Check if a query has errors
  const hasErrors = (m) => {
    return m.logs?.some(log => 
      log.error || 
      log.event?.includes('error') || 
      log.event?.includes('failed')
    ) || false;
  };

  useEffect(() => {
    const q = query(collection(db, 'metrics_queries'), orderBy('createdAt', 'desc'), limit(1000));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => {
        const data = d.data();
        list.push({ id: d.id, ...data });
      });
      setMetrics(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Load user data for mapping UIDs to emails
  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsub = onSnapshot(q, (snap) => {
      const userMap = {};
      snap.forEach((d) => {
        const data = d.data();
        userMap[d.id] = {
          email: data.email || d.id,
          displayName: data.displayName || null,
          userType: data.userType || 'user'
        };
      });
      setUsers(userMap);
    });
    return () => unsub();
  }, []);

  const filteredMetrics = useMemo(() => {
    let filtered = metrics;
    
    // Apply time range filter
    if (timeRange !== 'all') {
      const now = Date.now();
      const cutoff = {
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000
      }[timeRange];
      filtered = filtered.filter(m => {
        const ts = m.createdAt?.toMillis?.() || 0;
        return (now - ts) <= cutoff;
      });
    }
    
    // Apply no-results filter (complete failure - no fixtures found)
    if (filterNoResults) {
      filtered = filtered.filter(m => {
        const hasNoResults = (m.resultsCount === 0 || !m.resultsCount);
        const hasNoOddsMarker = m.logs?.some(log => 
          log.event === 'fixtures_found_no_odds' || 
          log.event?.includes('no_odds')
        );
        // True no results: 0 results AND no "fixtures found but no odds" marker
        return hasNoResults && !hasNoOddsMarker;
      });
    }
    
    // Apply no-odds-data filter (fixtures found but no odds posted)
    if (filterNoOddsData) {
      filtered = filtered.filter(m => {
        const hasNoOddsMarker = m.logs?.some(log => 
          log.event === 'fixtures_found_no_odds' || 
          log.event?.includes('no_odds') ||
          log.event?.includes('coming_soon')
        );
        return hasNoOddsMarker;
      });
    }
    
    // ⭐ Apply errors filter
    if (filterErrors) {
      filtered = filtered.filter(m => hasErrors(m));
    }
    
    return filtered;
  }, [metrics, timeRange, filterNoResults, filterNoOddsData, filterErrors]);

  const stats = useMemo(() => {
    if (!filteredMetrics.length) return null;
    const total = filteredMetrics.length;
    const positive = filteredMetrics.filter(m => m.feedback === 'up').length;
    const negative = filteredMetrics.filter(m => m.feedback === 'down').length;
    const avgDuration = Math.round(filteredMetrics.reduce((sum, m) => sum + (m.durationMs || 0), 0) / total);
    const avgResults = Math.round(filteredMetrics.reduce((sum, m) => sum + (m.resultsCount || 0), 0) / total);
    const broadenCount = filteredMetrics.filter(m => m.broaden).length;
    
    // ⭐ Track ticker clicks vs manual searches
    const tickerCount = filteredMetrics.filter(m => m.source === 'ticker').length;
    const searchCount = total - tickerCount;
    
    // ⭐ IMPROVED: Distinguish between true no results and no odds data
    // Now uses the new resultsWithoutOdds field if available
    const trueNoResultsCount = filteredMetrics.filter(m => {
      // If we have the new fields, use them for accurate detection
      if (m.resultsWithOdds !== undefined) {
        // True no results = no results at all (no odds AND no pending fixtures)
        return m.resultsCount === 0 && m.resultsWithOdds === 0 && (m.resultsWithoutOdds === 0 || !m.resultsWithoutOdds);
      }
      
      // Fallback for old data without new fields
      const hasNoResults = (m.resultsCount === 0 || !m.resultsCount);
      const hasNoOddsMarker = m.logs?.some(log => 
        log.event === 'fixtures_found_no_odds' || 
        log.event?.includes('no_odds') ||
        log.event?.includes('coming_soon')
      );
      return hasNoResults && !hasNoOddsMarker;
    }).length;
    
    // ⭐ IMPROVED: Count queries that found fixtures but odds aren't posted yet
    const noOddsDataCount = filteredMetrics.filter(m => {
      // If we have the new field, use it
      if (m.resultsWithoutOdds !== undefined) {
        return m.resultsWithoutOdds > 0;
      }
      
      // Fallback for old data
      const hasNoOddsMarker = m.logs?.some(log => 
        log.event === 'fixtures_found_no_odds' || 
        log.event?.includes('no_odds') ||
        log.event?.includes('coming_soon')
      );
      return hasNoOddsMarker;
    }).length;
    
    // Watch/notification count - queries that resulted in user watching fixtures
    const watchCount = filteredMetrics.filter(m => {
      return m.logs?.some(log => 
        log.event === 'fixture_watched' || 
        log.event?.includes('watch')
      );
    }).length;
    
    // User stats
    const uniqueUsers = new Set(filteredMetrics.map(m => m.uid || 'anon')).size;
    const topUsers = Object.entries(
      filteredMetrics.reduce((acc, m) => {
        const uid = m.uid || 'anon';
        acc[uid] = (acc[uid] || 0) + 1;
        return acc;
      }, {})
    )
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([uid, count]) => ({
        uid,
        count,
        email: users[uid]?.email || (uid === 'anon' ? 'Anonymous' : uid)
      }));
    
    // ⭐ Count errors
    const errorCount = filteredMetrics.filter(m => hasErrors(m)).length;
    
    // ⭐ Top search terms
    const searchTerms = filteredMetrics.reduce((acc, m) => {
      const term = (m.prompt || '').toLowerCase().trim();
      if (term) acc[term] = (acc[term] || 0) + 1;
      return acc;
    }, {});
    const topSearches = Object.entries(searchTerms)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([term, count]) => ({ term, count }));
    
    return { 
      total, 
      positive, 
      negative, 
      avgDuration, 
      avgResults, 
      broadenCount, 
      trueNoResultsCount, 
      noOddsDataCount, 
      watchCount,
      uniqueUsers, 
      topUsers,
      errorCount,
      topSearches,
      tickerCount, // ⭐ NEW: Track ticker clicks
      searchCount  // ⭐ NEW: Track manual searches
    };
  }, [filteredMetrics, users]);
  
  // ⭐ Generate AI summary of metrics
  const generateAiSummary = async () => {
    if (!stats || stats.total === 0) return;
    setSummaryLoading(true);
    
    try {
      const successRate = stats.total > 0 ? ((stats.total - stats.trueNoResultsCount) / stats.total * 100).toFixed(0) : 0;
      const avgDurationSec = (stats.avgDuration / 1000).toFixed(1);
      
      // Create a plain-text summary (no API call needed for basic summary)
      const summary = {
        headline: stats.errorCount > 0 
          ? `⚠️ ${stats.errorCount} errors detected in ${stats.total} queries`
          : stats.trueNoResultsCount > stats.total * 0.1
          ? `📊 ${stats.total} queries with ${stats.trueNoResultsCount} no-result cases`
          : `✅ ${stats.total} queries processed with ${successRate}% success`,
        
        performance: avgDurationSec < 30 
          ? `Query performance is good (${avgDurationSec}s average)`
          : avgDurationSec < 45
          ? `Query performance is acceptable (${avgDurationSec}s average)`
          : `Query performance needs attention (${avgDurationSec}s average)`,
        
        engagement: stats.positive > stats.negative
          ? `Users are responding positively (${stats.positive} 👍 vs ${stats.negative} 👎)`
          : stats.negative > stats.positive
          ? `Some user dissatisfaction (${stats.negative} 👎 vs ${stats.positive} 👍) - review failed queries`
          : `Mixed user feedback (${stats.positive} 👍 / ${stats.negative} 👎)`,
        
        topIssue: stats.trueNoResultsCount > 0
          ? `${stats.trueNoResultsCount} queries returned no results - check if users are searching for unsupported sports/leagues`
          : stats.noOddsDataCount > 0
          ? `${stats.noOddsDataCount} queries found games but no odds - likely future games`
          : 'No major issues detected',
          
        recommendation: stats.errorCount > 0
          ? 'Review error logs below and check API connectivity'
          : stats.avgDuration > 45000
          ? 'Consider optimizing cache or adding more pre-aggregated data'
          : stats.trueNoResultsCount > stats.total * 0.2
          ? 'Many empty searches - may need to expand coverage or improve query understanding'
          : 'System is performing well - continue monitoring'
      };
      
      setAiSummary(summary);
    } catch (err) {
      console.error('AI summary error:', err);
    } finally {
      setSummaryLoading(false);
    }
  };
  
  // ⭐ Auto-generate summary when stats change
  useEffect(() => {
    if (stats && stats.total > 0 && !aiSummary) {
      generateAiSummary();
    }
  }, [stats]);

  return (
    <View style={styles.tabWrap}>
      <Text style={styles.sectionTitle}>Query Metrics {loading ? '(loading...)' : `(${metrics.length} total)`}</Text>
      
      <View style={[styles.row, { marginBottom: 16 }]}>
        <Text style={styles.label}>Time Range:</Text>
        {['24h', '7d', '30d', 'all'].map((range) => (
          <TouchableOpacity
            key={range}
            onPress={() => setTimeRange(range)}
            style={[styles.tag, timeRange === range ? styles.tagActive : null]}
          >
            <Text style={[styles.tagText, timeRange === range ? styles.tagTextActive : null]}>
              {range === '24h' ? 'Last 24h' : range === '7d' ? 'Last 7d' : range === '30d' ? 'Last 30d' : 'All Time'}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      
      <View style={[styles.row, { marginBottom: 16, flexWrap: 'wrap', gap: 8 }]}>
        <TouchableOpacity
          onPress={() => {
            setFilterNoResults(!filterNoResults);
            if (!filterNoResults) { setFilterNoOddsData(false); setFilterErrors(false); }
          }}
          style={[styles.tag, filterNoResults ? styles.tagActive : null]}
        >
          <Text style={[styles.tagText, filterNoResults ? styles.tagTextActive : null]}>
            {filterNoResults ? '✓ ' : ''}No Results {stats?.trueNoResultsCount ? `(${stats.trueNoResultsCount})` : ''}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            setFilterNoOddsData(!filterNoOddsData);
            if (!filterNoOddsData) { setFilterNoResults(false); setFilterErrors(false); }
          }}
          style={[styles.tag, filterNoOddsData ? styles.tagActive : null]}
        >
          <Text style={[styles.tagText, filterNoOddsData ? styles.tagTextActive : null]}>
            {filterNoOddsData ? '✓ ' : ''}No Odds {stats?.noOddsDataCount ? `(${stats.noOddsDataCount})` : ''}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            setFilterErrors(!filterErrors);
            if (!filterErrors) { setFilterNoResults(false); setFilterNoOddsData(false); }
          }}
          style={[styles.tag, filterErrors ? { backgroundColor: '#EF4444' } : null]}
        >
          <Text style={[styles.tagText, filterErrors ? styles.tagTextActive : null]}>
            {filterErrors ? '✓ ' : ''}Errors {stats?.errorCount ? `(${stats.errorCount})` : ''}
          </Text>
        </TouchableOpacity>
      </View>
      
      {/* ⭐ AI Summary Card */}
      {aiSummary && (
        <View style={[styles.card, { marginBottom: 16, borderLeftWidth: 4, borderLeftColor: uiVar('adm-accent', '#8B5CF6') }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Text style={{ fontSize: 16 }}>✨</Text>
            <Text style={[styles.groupHeader, { marginTop: 0, marginBottom: 0 }]}>AI Summary</Text>
            <TouchableOpacity onPress={generateAiSummary} style={{ marginLeft: 'auto' }}>
              <Text style={{ color: uiVar('adm-accent', '#8B5CF6'), fontSize: 12 }}>↻ Refresh</Text>
            </TouchableOpacity>
          </View>
          <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 16, fontWeight: '600', marginBottom: 12 }}>{aiSummary.headline}</Text>
          <View style={{ gap: 8 }}>
            <Text style={{ color: uiVar('adm-muted', '#94A3B8'), fontSize: 13 }}>📊 {aiSummary.performance}</Text>
            <Text style={{ color: uiVar('adm-muted', '#94A3B8'), fontSize: 13 }}>💬 {aiSummary.engagement}</Text>
            <Text style={{ color: uiVar('adm-muted', '#94A3B8'), fontSize: 13 }}>🔍 {aiSummary.topIssue}</Text>
          </View>
          <View style={{ marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.1)' }}>
            <Text style={{ color: '#10B981', fontSize: 13, fontWeight: '500' }}>💡 {aiSummary.recommendation}</Text>
          </View>
        </View>
      )}

      {stats ? (
        <View style={[styles.card, { marginBottom: 16 }]}>
          <Text style={styles.groupHeader}>Statistics</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Total Queries</Text>
              <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 24, fontWeight: '700' }}>{stats.total}</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Positive Feedback</Text>
              <Text style={{ color: '#4ade80', fontSize: 24, fontWeight: '700' }}>
                {stats.positive} <Text style={{ fontSize: 14 }}>👍</Text>
              </Text>
              <Text style={styles.caption}>{((stats.positive / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Negative Feedback</Text>
              <Text style={{ color: '#f87171', fontSize: 24, fontWeight: '700' }}>
                {stats.negative} <Text style={{ fontSize: 14 }}>👎</Text>
              </Text>
              <Text style={styles.caption}>{((stats.negative / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Avg Duration</Text>
              <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 24, fontWeight: '700' }}>{stats.avgDuration}ms</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Avg Results</Text>
              <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 24, fontWeight: '700' }}>{stats.avgResults}</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Broadened Queries</Text>
              <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 24, fontWeight: '700' }}>{stats.broadenCount}</Text>
              <Text style={styles.caption}>{((stats.broadenCount / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Unique Users</Text>
              <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 24, fontWeight: '700' }}>{stats.uniqueUsers}</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>True No Results</Text>
              <Text style={{ color: '#f87171', fontSize: 24, fontWeight: '700' }}>
                {stats.trueNoResultsCount} <Text style={{ fontSize: 14 }}>⚠️</Text>
              </Text>
              <Text style={styles.caption}>{((stats.trueNoResultsCount / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>No Odds Data</Text>
              <Text style={{ color: '#fbbf24', fontSize: 24, fontWeight: '700' }}>
                {stats.noOddsDataCount} <Text style={{ fontSize: 14 }}>🕐</Text>
              </Text>
              <Text style={styles.caption}>{((stats.noOddsDataCount / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>Watched Fixtures</Text>
              <Text style={{ color: '#4CAF50', fontSize: 24, fontWeight: '700' }}>
                {stats.watchCount} <Text style={{ fontSize: 14 }}>👁️</Text>
              </Text>
              <Text style={styles.caption}>{((stats.watchCount / stats.total) * 100).toFixed(1)}%</Text>
            </View>
            {/* ⭐ NEW: Ticker click tracking */}
            <View style={{ minWidth: 120 }}>
              <Text style={styles.caption}>From Ticker</Text>
              <Text style={{ color: '#60A5FA', fontSize: 24, fontWeight: '700' }}>
                {stats.tickerCount || 0} <Text style={{ fontSize: 14 }}>📺</Text>
              </Text>
              <Text style={styles.caption}>{stats.total > 0 ? ((stats.tickerCount / stats.total) * 100).toFixed(1) : 0}%</Text>
            </View>
          </View>
          
          <View style={{ flexDirection: 'row', gap: 32, marginTop: 16 }}>
            {/* Top Users */}
            {stats.topUsers.length > 0 && (
              <View style={{ flex: 1 }}>
                <Text style={[styles.groupHeader, { marginTop: 0 }]}>Top Users</Text>
                {stats.topUsers.map((user, idx) => (
                  <View key={user.uid} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, width: 20 }}>#{idx + 1}</Text>
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, flex: 1 }} numberOfLines={1}>{user.email}</Text>
                    <Text style={{ color: '#4ade80', fontSize: 13, fontWeight: '700' }}>{user.count}</Text>
                  </View>
                ))}
              </View>
            )}
            
            {/* ⭐ Top Searches */}
            {stats.topSearches?.length > 0 && (
              <View style={{ flex: 1 }}>
                <Text style={[styles.groupHeader, { marginTop: 0 }]}>Top Searches</Text>
                {stats.topSearches.map((search, idx) => (
                  <View key={search.term} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, width: 20 }}>#{idx + 1}</Text>
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, flex: 1 }} numberOfLines={1}>
                      {search.term.length > 30 ? search.term.substring(0, 30) + '...' : search.term}
                    </Text>
                    <Text style={{ color: '#60A5FA', fontSize: 13, fontWeight: '700' }}>{search.count}×</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      ) : null}

      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text style={styles.groupHeader}>Recent Queries</Text>
        <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>
          Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredMetrics.length)}-{Math.min(currentPage * itemsPerPage, filteredMetrics.length)} of {filteredMetrics.length}
        </Text>
      </View>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 8 }}>
        {filteredMetrics.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((m) => {
          const uid = m.uid || 'anon';
          const userInfo = users[uid];
          const userDisplay = userInfo ? (userInfo.email) : (uid === 'anon' ? 'Anonymous' : uid);
          const isAdmin = userInfo?.userType === 'admin';
          const hasNoResults = (m.resultsCount === 0 || !m.resultsCount);
          const queryHasErrors = hasErrors(m);
          const isExpanded = expandedQueryId === m.id;
          
          return (
            <View 
              key={m.id} 
              style={[
                styles.userRow, 
                { flexDirection: 'column', alignItems: 'stretch' },
                hasNoResults && { borderColor: 'rgba(248, 113, 113, 0.3)', backgroundColor: 'rgba(248, 113, 113, 0.05)' },
                queryHasErrors && { borderColor: 'rgba(239, 68, 68, 0.5)', borderWidth: 1, borderLeftWidth: 4 }
              ]}
            >
              <TouchableOpacity 
                onPress={() => setExpandedQueryId(isExpanded ? null : m.id)}
                style={{ flex: 1 }}
                activeOpacity={0.7}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Text style={styles.userEmail}>{m.prompt || 'No prompt'}</Text>
                      {queryHasErrors && (
                        <View style={{ paddingHorizontal: 6, paddingVertical: 2, backgroundColor: 'rgba(239, 68, 68, 0.2)', borderRadius: 4 }}>
                          <Text style={{ color: '#EF4444', fontSize: 10, fontWeight: '700' }}>❌ ERROR</Text>
                        </View>
                      )}
                      {hasNoResults && !queryHasErrors && <Text style={{ fontSize: 14 }}>⚠️</Text>}
                      {m.logs && Array.isArray(m.logs) && m.logs.length > 0 && (
                        <View style={{ paddingHorizontal: 6, paddingVertical: 2, backgroundColor: 'rgba(78, 205, 196, 0.2)', borderRadius: 4 }}>
                          <Text style={{ color: '#4ecdc4', fontSize: 10, fontWeight: '700' }}>{m.logs.length} steps</Text>
                        </View>
                      )}
                    </View>
                    <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', marginTop: 2 }}>
                      <Text style={[styles.userMeta, { fontSize: 12 }]}>
                        👤 {userDisplay}
                      </Text>
                      {isAdmin && (
                        <View style={{ paddingHorizontal: 6, paddingVertical: 2, backgroundColor: uiVar('adm-accent-wash-20', 'rgba(139, 92, 246, 0.2)'), borderRadius: 4 }}>
                          <Text style={{ color: uiVar('adm-accent', '#a78bfa'), fontSize: 10, fontWeight: '700' }}>ADMIN</Text>
                        </View>
                      )}
                    </View>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                    {m.feedback === 'up' && <Text style={{ fontSize: 18 }}>👍</Text>}
                    {m.feedback === 'down' && <Text style={{ fontSize: 18 }}>👎</Text>}
                    {!m.feedback && <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>no feedback</Text>}
                    <View style={{ backgroundColor: isExpanded ? 'rgba(78, 205, 196, 0.2)' : 'rgba(255,255,255,0.1)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 }}>
                      <Text style={{ color: isExpanded ? '#4ecdc4' : 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: '700' }}>
                        {isExpanded ? '▲ COLLAPSE' : '▼ EXPAND'}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={{ flexDirection: 'row', gap: 16, flexWrap: 'wrap' }}>
                  <Text style={styles.userMeta}>Duration: {m.durationMs || 0}ms</Text>
                  <Text style={[styles.userMeta, hasNoResults && { color: '#f87171', fontWeight: '700' }]}>
                    Results: {m.resultsCount || 0}
                    {/* ⭐ Show breakdown if we have it */}
                    {(m.resultsWithOdds !== undefined || m.resultsWithoutOdds !== undefined) && (
                      <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                        {' '}({m.resultsWithOdds || 0} odds, {m.resultsWithoutOdds || 0} pending)
                      </Text>
                    )}
                  </Text>
                  {m.broaden && <Text style={[styles.userMeta, { color: '#fbbf24' }]}>Broadened</Text>}
                  {/* ⭐ Show source badge (ticker vs search) */}
                  {m.source === 'ticker' && (
                    <View style={{ paddingHorizontal: 6, paddingVertical: 2, backgroundColor: 'rgba(96, 165, 250, 0.2)', borderRadius: 4 }}>
                      <Text style={{ color: '#60A5FA', fontSize: 10, fontWeight: '700' }}>📺 TICKER</Text>
                    </View>
                  )}
                  <Text style={styles.userMeta}>
                    {m.createdAt?.toDate?.() ? m.createdAt.toDate().toLocaleString() : 'Unknown date'}
                  </Text>
                </View>
              </TouchableOpacity>
              
              {/* ⭐ Expanded Logs Section - Plain Language */}
              {isExpanded && m.logs && Array.isArray(m.logs) && m.logs.length > 0 && (
                <View style={{ marginTop: 12, padding: 12, backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)' }}>
                  <Text style={{ color: uiVar('adm-ink', '#fff'), fontWeight: '700', marginBottom: 8 }}>Query Journey:</Text>
                  <View style={{ gap: 6 }}>
                    {m.logs.map((log, idx) => {
                      const isError = log.error || log.event?.includes('error') || log.event?.includes('failed');
                      const translatedEvent = translateLogEvent(log);
                      
                      return (
                        <View 
                          key={idx} 
                          style={{ 
                            flexDirection: 'row', 
                            alignItems: 'flex-start',
                            gap: 8,
                            paddingVertical: 4,
                            paddingHorizontal: 8,
                            backgroundColor: isError ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                            borderRadius: 4,
                            borderLeftWidth: isError ? 3 : 0,
                            borderLeftColor: '#EF4444'
                          }}
                        >
                          <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, width: 50 }}>
                            {log.timestamp ? new Date(log.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'}) : ''}
                          </Text>
                          <View style={{ flex: 1 }}>
                            <Text style={{ 
                              color: isError ? '#f87171' : uiVar('adm-ink', '#E2E8F0'), 
                              fontSize: 13,
                              fontWeight: isError ? '600' : '400'
                            }}>
                              {translatedEvent}
                            </Text>
                            {isError && log.error && (
                              <Text style={{ color: '#FCA5A5', fontSize: 11, marginTop: 2 }}>
                                {log.error}
                              </Text>
                            )}
                            {log.durationMs && (
                              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>
                                Took {log.durationMs}ms
                              </Text>
                            )}
                          </View>
                        </View>
                      );
                    })}
                  </View>
                  
                  {/* Quick summary at bottom */}
                  <View style={{ marginTop: 12, paddingTop: 8, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.1)' }}>
                    <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
                      Summary: {m.logs.length} steps • {m.durationMs || 0}ms total • {m.resultsCount || 0} results
                      {hasErrors(m) && ' • ⚠️ Has errors'}
                    </Text>
                  </View>
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>
      
      {/* Pagination Controls */}
      {filteredMetrics.length > itemsPerPage && (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16, paddingVertical: 12 }}>
          <TouchableOpacity
            onPress={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={[styles.btn, currentPage === 1 && { opacity: 0.4 }]}
          >
            <Text style={styles.btnText}>← Previous</Text>
          </TouchableOpacity>
          <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 14, fontWeight: '600' }}>
            Page {currentPage} of {Math.ceil(filteredMetrics.length / itemsPerPage)}
          </Text>
          <TouchableOpacity
            onPress={() => setCurrentPage(p => Math.min(Math.ceil(filteredMetrics.length / itemsPerPage), p + 1))}
            disabled={currentPage >= Math.ceil(filteredMetrics.length / itemsPerPage)}
            style={[styles.btn, currentPage >= Math.ceil(filteredMetrics.length / itemsPerPage) && { opacity: 0.4 }]}
          >
            <Text style={styles.btnText}>Next →</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

